const STORAGE_PREFIX = 'esgcaffe_login_lock_until:';

function storageKey(email: string): string {
  return `${STORAGE_PREFIX}${email.trim().toLowerCase()}`;
}

/** ISO timestamp until which login is blocked for this email (server `lockedUntil`). */
export function readStoredLockUntilIso(email: string): string | null {
  if (typeof localStorage === 'undefined' || !email.trim()) return null;
  const raw = localStorage.getItem(storageKey(email));
  if (!raw) return null;
  const end = new Date(raw).getTime();
  if (Number.isNaN(end) || end <= Date.now()) {
    localStorage.removeItem(storageKey(email));
    return null;
  }
  return raw;
}

export function writeStoredLockUntilIso(email: string, lockedUntilIso: string): void {
  if (typeof localStorage === 'undefined' || !email.trim()) return;
  localStorage.setItem(storageKey(email), lockedUntilIso);
}

export function clearStoredLockUntil(email: string): void {
  if (typeof localStorage === 'undefined' || !email.trim()) return;
  localStorage.removeItem(storageKey(email));
}

export function formatLockDuration(remainingMs: number): string {
  if (remainingMs <= 0) return '0 seconds';
  const secTotal = Math.ceil(remainingMs / 1000);
  const m = Math.floor(secTotal / 60);
  const s = secTotal % 60;
  const parts: string[] = [];
  if (m > 0) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  parts.push(`${s} second${s === 1 ? '' : 's'}`);
  return parts.join(' ');
}

/** MM:SS countdown from remaining milliseconds (e.g. 14:32). */
export function formatLockCountdownMmSs(remainingMs: number): string {
  if (remainingMs <= 0) return '00:00';
  const secTotal = Math.max(0, Math.ceil(remainingMs / 1000));
  const m = Math.floor(secTotal / 60);
  const s = secTotal % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Read lock deadline from API error bodies (top-level or nested `data`). */
export function extractLockedUntilIso(body: unknown): string | undefined {
  if (body == null || typeof body !== 'object') return undefined;
  const o = body as Record<string, unknown>;
  const top = o.lockedUntil ?? o.locked_until;
  if (typeof top === 'string' && top.length > 0) return top;
  const inner = o.data;
  if (inner != null && typeof inner === 'object') {
    const i = inner as Record<string, unknown>;
    const v = i.lockedUntil ?? i.locked_until;
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

export function remainingMsFromIso(lockedUntilIso: string): number {
  const end = new Date(lockedUntilIso).getTime();
  if (Number.isNaN(end)) return 0;
  return Math.max(0, end - Date.now());
}
